import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import moment from "moment"
import "moment/locale/pt-br"
import birthdayService from "../../services/data/birthday.service"
import companyAnniversaryService from "../../services/data/company-anniversary.service"

const BD_LOG_LABELS = {
    monthly_email: "Email Mensal",
    ecard: "E-card",
    missing_alert_email: "Alerta Faltante (Email)",
    missing_alert_sms: "Alerta Faltante (SMS)",
    gift: "Presente",
}

const AN_LOG_LABELS = {
    ecard: "E-card",
    monthly_email: "Email Mensal",
    superior_notification: "Notificação para Gestor",
}

export default function BirthdayReport() {
    const companyId = localStorage.getItem("companyId")
    const [logs, setLogs] = useState([])
    const [annivLogs, setAnnivLogs] = useState([])
    const [loading, setLoading] = useState(false)
    const [missing, setMissing] = useState([])
    const [notifying, setNotifying] = useState(false)
    const [notified, setNotified] = useState(false)

    useEffect(() => {
        loadLogs()
        loadMissing()
    }, [])

    function loadLogs() {
        setLoading(true)
        const payload = { company_id: companyId }
        Promise.all([
            birthdayService.getLogs(payload),
            companyAnniversaryService.getLogs(payload).catch(() => ({ results: [] })),
        ])
            .then(([bd, an]) => {
                setLogs(bd.results || [])
                setAnnivLogs(an.results || [])
            })
            .catch(console.error)
            .finally(() => setLoading(false))
    }

    function loadMissing() {
        birthdayService.getMissing({ company_id: companyId })
            .then(setMissing)
            .catch(console.error)
    }

    function handleNotifyMissing() {
        setNotifying(true)
        birthdayService.notifyMissing({ company_id: companyId })
            .then(() => setNotified(true))
            .catch(console.error)
            .finally(() => setNotifying(false))
    }

    return (
        <div>
            {missing.length > 0 && (
                <div className="card border-warning mb-4">
                    <div className="card-body">
                        <h6 className="fw-bold text-default mb-2">
                            <FontAwesomeIcon icon={solid("triangle-exclamation")} className="me-2 text-warning" />
                            Colaboradores sem data de nascimento ({missing.length})
                        </h6>
                        <div className="table-responsive mb-3">
                            <table className="table table-sm table-borderless">
                                <tbody>
                                    {missing.slice(0, 10).map(emp => (
                                        <tr key={emp.id} className="border-bottom">
                                            <td className="fw-semibold small">{emp.name}</td>
                                            <td className="text-muted small">{emp.email || "sem email"}</td>
                                            <td className="text-muted small">{emp.phone || "sem telefone"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {missing.length > 10 && (
                                <div className="text-muted small">+{missing.length - 10} mais</div>
                            )}
                        </div>
                        {notified
                            ? <div className="alert alert-success py-2 small mb-0">Notificações enviadas!</div>
                            : <button className="btn btn-sm btn-warning" onClick={handleNotifyMissing} disabled={notifying}>
                                <FontAwesomeIcon icon={solid("bell")} className="me-1" />
                                {notifying ? "Enviando..." : "Notificar todos por Email/SMS"}
                            </button>
                        }
                    </div>
                </div>
            )}

            <h6 className="fw-bold text-default mb-3">
                <FontAwesomeIcon icon={solid("cake-candles")} className="me-2 text-green" />
                Histórico — Aniversários de Nascimento
            </h6>

            {loading && <div className="text-muted small">Carregando...</div>}

            {!loading && logs.length === 0 && (
                <div className="text-muted small text-center py-3">Nenhum registro ainda.</div>
            )}

            {!loading && logs.length > 0 && (
                <div className="table-responsive mb-5">
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr className="text-muted small">
                                <th>Colaborador</th>
                                <th>Tipo</th>
                                <th>Status</th>
                                <th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.map(log => (
                                <tr key={log.id} className="small">
                                    <td className="fw-semibold">{log.employee_name}</td>
                                    <td>{BD_LOG_LABELS[log.log_type] || log.log_type}</td>
                                    <td>
                                        <span className={`badge ${log.status === "sent" ? "bg-success" : "bg-danger"}`}>
                                            {log.status === "sent" ? "Enviado" : "Falhou"}
                                        </span>
                                    </td>
                                    <td className="text-muted">{moment(log.sent_at).format("DD/MM/YYYY HH:mm")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <hr />

            <h6 className="fw-bold text-default mb-3 mt-4">
                <FontAwesomeIcon icon={solid("building")} className="me-2 text-green" />
                Histórico — Aniversários por Tempo de Empresa
            </h6>

            {!loading && annivLogs.length === 0 && (
                <div className="text-muted small text-center py-3">Nenhum registro ainda.</div>
            )}

            {!loading && annivLogs.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-sm table-hover">
                        <thead>
                            <tr className="text-muted small">
                                <th>Colaborador</th>
                                <th>Anos</th>
                                <th>Tipo</th>
                                <th>Destinatário</th>
                                <th>Status</th>
                                <th>Data</th>
                            </tr>
                        </thead>
                        <tbody>
                            {annivLogs.map(log => (
                                <tr key={log.id} className="small">
                                    <td className="fw-semibold">{log.employee_name}</td>
                                    <td>{log.years_in_company > 0 ? `${log.years_in_company} ano${log.years_in_company !== 1 ? "s" : ""}` : "—"}</td>
                                    <td>{AN_LOG_LABELS[log.log_type] || log.log_type}</td>
                                    <td className="text-muted">{log.recipient_name || "—"}</td>
                                    <td>
                                        <span className={`badge ${log.status === "sent" ? "bg-success" : "bg-danger"}`}>
                                            {log.status === "sent" ? "Enviado" : "Falhou"}
                                        </span>
                                    </td>
                                    <td className="text-muted">{moment(log.sent_at).format("DD/MM/YYYY HH:mm")}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    )
}
